import { isEmpty } from '@utils/common';
import { isSameDayInUTC } from '@utils/date';
import { getOoklaMeasurementType } from '@utils/ookla/getOoklaMeasurementType';

import { IMapOoklaFiltersDto } from '@interfaces/map/dto/mapFiltersDTO';
import { IOoklaData } from '@interfaces/platform/ookla';

import { EOoklaDisplayTypes, EOoklaServiceLevel, OoklaLegendRange } from '@constants/ookla';

export interface IFilterOoklaDataParams {
	data: IOoklaData[];
	filters: IMapOoklaFiltersDto;
}

export interface IFilterOoklaDataReturn {
	assetRelatedData: Record<string, IOoklaData>;
	coverageRelatedData: Record<string, IOoklaData>;
}

const ASSET_ID_NULL = '0';

export const filterOoklaData = ({
	data,
	filters,
}: IFilterOoklaDataParams): IFilterOoklaDataReturn => {
	const newData: IFilterOoklaDataReturn = {
		assetRelatedData: {},
		coverageRelatedData: {},
	};

	if (isEmpty(filters)) return newData;

	const {
		deviceType,
		measurementType,
		technology,
		networkOperator,
		measurementName,
	} = filters;

	const isValidValue = ({
		value,
		measurement,
		isServiceLevelGood,
		isServiceLevelVariable,
		isServiceLevelNoService,
	}: {
		value: string;
		measurement: string;
		isServiceLevelGood: boolean;
		isServiceLevelVariable: boolean;
		isServiceLevelNoService: boolean;
	}) => {
		const numericValue = parseFloat(value);
		const measurementType = getOoklaMeasurementType(measurement);

		if ((measurementType && !OoklaLegendRange[measurementType]) || !measurementType) {
			return false;
		}

		const range = OoklaLegendRange[measurementType];

		for (const { min, max, level } of range) {
			if (numericValue >= min && numericValue <= max) {
				if (
					(isServiceLevelGood && level === EOoklaServiceLevel.good) ||
					(isServiceLevelVariable && level === EOoklaServiceLevel.variable) ||
					(isServiceLevelNoService && level === EOoklaServiceLevel.noService)
				) {
					return true;
				}
			}
		}

		return false;
	};

	const isMatchingOokla = (ookla: IOoklaData): boolean => {
		if (!filters.dateFrom) {
			return false;
		}

		return (
			ookla.deviceType.name === deviceType &&
			ookla.measurementType.name === measurementType &&
			ookla.technology === technology &&
			ookla.operator === networkOperator?.name &&
			ookla.measurement === measurementName?.name &&
			isSameDayInUTC(ookla.measuredAt, filters.dateFrom) &&
			isValidValue({
				value: ookla.value,
				measurement: ookla.measurement,
				isServiceLevelGood: !!filters.serviceLevelGood,
				isServiceLevelVariable: !!filters.serviceLevelVariable,
				isServiceLevelNoService: !!filters.serviceLevelNoService,
			})
		);
	};

	data.forEach(ookla => {
		if (filters.type === EOoklaDisplayTypes.colorRelatedAssets) {
			if (isMatchingOokla(ookla) && ookla.assetId && ookla.assetId !== ASSET_ID_NULL) {
				newData.assetRelatedData[ookla.assetId] = ookla;
			} else if (isMatchingOokla(ookla) && ookla.assetId === ASSET_ID_NULL) {
				newData.coverageRelatedData[ookla.id] = ookla;
			}
		}

		if (filters.type === EOoklaDisplayTypes.showCoverage) {
			if (isMatchingOokla(ookla)) {
				newData.coverageRelatedData[ookla.id] = ookla;
			}
		}
	});

	return newData;
};
